"use client";

import React, { useState, useEffect, useMemo } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useReviews } from "@/hooks/useReviews";
import { ReviewForm } from "@/components/reviews/ReviewForm";
import { RatingSummary } from "@/components/reviews/RatingSummary";
const SPARK_ICON_URL = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685663/img/construction_spark_icon.png";
export default function VoicesOfSatisfaction() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  const {
    reviews,
    loading,
    error,
    refetch
  } = useReviews();
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check user preference for reduced motion
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  // Configure autoplay plugin (delay 6000ms, pause on hover, resume after interaction)
  const autoplayPlugin = useMemo(() => {
    return Autoplay({
      delay: 6000,
      stopOnInteraction: false,
      stopOnMouseEnter: true
    });
  }, []);
  const plugins = useMemo(() => {
    if (reviews.length > 1 && !prefersReducedMotion) {
      return [autoplayPlugin];
    }
    return [];
  }, [reviews.length, prefersReducedMotion, autoplayPlugin]);

  // Track active slide
  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => {
      setSelectedIndex(carouselApi.selectedScrollSnap());
    };
    const onReInit = () => {
      setSelectedIndex(carouselApi.selectedScrollSnap());
    };
    carouselApi.on("select", onSelect);
    carouselApi.on("reInit", onReInit);
    return () => {
      carouselApi.off("select", onSelect);
      carouselApi.off("reInit", onReInit);
    };
  }, [carouselApi]);

  // When a review is submitted, rewind carousel to first slide (newest)
  const handleReviewSuccess = () => {
    refetch();
    if (carouselApi) {
      carouselApi.scrollTo(0);
    }
  };
  return <section data-section-id="225769" id="reviews" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border" ref={ref}>
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-8">
        <div className={`transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0 motion-reduce:opacity-100"}`}>
          <div className="max-w-3xl mb-10 md:mb-14 text-left">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal text-foreground leading-[1.15] tracking-tight mb-3">
              What customers say
            </h2>
            <div className="mt-2">
              <RatingSummary />
            </div>
          </div>

          {/* Loading state */}
          {loading && reviews.length === 0 && <div className="flex flex-col items-start py-12 text-muted-foreground gap-3">
              <Loader2 className="size-6 animate-spin text-primary" />
              <p className="font-mono text-xs uppercase tracking-wider">
                Loading reviews...
              </p>
            </div>}

          {/* Error state */}
          {error && reviews.length === 0 && <div className="p-6 border border-destructive/40 bg-destructive/10 rounded-xs max-w-xl text-left">
              <AlertCircle className="size-6 text-destructive mb-3" />
              <p className="text-destructive font-medium mb-3 text-sm">{error}</p>
              <button type="button" onClick={() => refetch()} className="text-xs uppercase font-mono font-bold tracking-widest text-primary hover:underline cursor-pointer">
                Try Again
              </button>
            </div>}

          {/* Empty state */}
          {!loading && !error && reviews.length === 0 && <div className="py-10 px-6 border border-border rounded-xs bg-card text-left max-w-xl">
              <p className="text-base font-sans text-muted-foreground">
                No reviews yet. Be the first to share your experience with us.
              </p>
            </div>}

          {/* Reviews Carousel */}
          {reviews.length > 0 && <div className="relative mb-14">
              <Carousel setApi={setCarouselApi} plugins={plugins} opts={{
            align: "start",
            loop: reviews.length > 1
          }} className="w-full relative">
                <CarouselContent>
                  {reviews.map((review, i) => {
                const rating = Math.max(1, Math.min(5, Number(review.rating) || 5));
                return <CarouselItem key={review.id} data-index={i} className="md:basis-1/2 lg:basis-1/2">
                        <div className="p-6 md:p-8 rounded-xs bg-card border border-border h-full flex flex-col justify-between">
                          <div>
                            {/* Spark rating icons */}
                            <div className="flex items-center gap-1.5 mb-4" aria-label={`Rating: ${rating} out of 5 stars`}>
                              {[1, 2, 3, 4, 5].map(starIndex => <div key={starIndex} data-index={starIndex}>
                                  <img src={SPARK_ICON_URL} alt="Rating star" className={`size-4 object-contain ${starIndex <= rating ? "opacity-100" : "opacity-20 grayscale"}`} />
                                </div>)}
                            </div>

                            {/* Review text */}
                            <p className="font-sans text-foreground leading-relaxed text-base break-words whitespace-pre-line mb-6">
                              "{review.review}"
                            </p>
                          </div>

                          {/* Reviewer name and project */}
                          <div className="pt-4 border-t border-border flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
                            <span className="font-serif text-foreground text-base font-normal">
                              {review.name}
                            </span>
                            {review.project && <span className="text-muted-foreground font-mono text-xs uppercase tracking-wider">
                                {review.project}
                              </span>}
                          </div>
                        </div>
                      </CarouselItem>;
              })}
                </CarouselContent>

                {reviews.length > 1 && <div className="flex items-center gap-2 mt-6">
                    <CarouselPrevious className="static translate-y-0 bg-secondary border border-border hover:bg-muted text-foreground rounded-xs size-9" />
                    <CarouselNext className="static translate-y-0 bg-secondary border border-border hover:bg-muted text-foreground rounded-xs size-9" />
                    <span className="text-xs font-mono text-muted-foreground ml-3 uppercase tracking-wider">
                      {selectedIndex + 1} of {reviews.length}
                    </span>
                  </div>}
              </Carousel>
            </div>}

          {/* Share your experience block */}
          <div className="pt-10 border-t border-border max-w-2xl text-left">
            <div className="mb-6">
              <h3 className="font-serif text-2xl font-normal text-foreground mb-2">
                Share your experience
              </h3>
              <p className="text-sm text-muted-foreground font-sans">
                Left feedback helps us and helps neighbors looking for honest contracting services.
              </p>
            </div>

            <div className="bg-card border border-border rounded-xs p-6 md:p-8">
              <ReviewForm onSuccess={handleReviewSuccess} />
            </div>
          </div>
        </div>
      </div>
    </section>;
}